import { documentAPI } from "api"
import { ianaTimeZones } from "data"
import copy from "fast-copy"
import { getErrorMessage, getPrepiBotBaseURL, useAxios } from "functions"
import { useRef } from "react"
import toast from "react-hot-toast"
import { option } from "typings"
import { useAuth } from "../../../context/authContext"
import { confirmDangerModal } from "../../../global/constants/modals"
import { useModal } from "../../../global/hooks"
import Popout from "../../1_atom/autoComplete/popOut"
import Button from "../../1_atom/buttons/button"
import Tag from "../../1_atom/tag/tag"
import TextInput from "../../1_atom/textInput/textInput"
import ConfirmDangerModalBottom from "../modals/confirmDangerModal"
import { SettingSection } from "./settingSection"

const GeneralSettings = () => {
  const [ triggerModal, closeModal ] = useModal()
  const auth = useAuth()
  const userAPI = documentAPI().userAPI
  const axios = useAxios({
    getBearerToken: auth.getFirebaseAuthToken,
    baseURL: getPrepiBotBaseURL()
  })

  const DiscordChannels = () => {
    const channels = copy(auth?.userData?.discordChannels)
    if(!channels || channels?.length < 1) return <></>
    channels.sort((a, b) => {
      if(a?.guildName > b?.guildName) return 1
      if(a?.guildName < b?.guildName) return -1
      if(a?.channelName > b?.channelName) return 1
      if(a?.channelName < b?.channelName) return -1
      return 0
    })
    return (
      <SettingSection
        title="Discord channels"
        description="Used in automations and when creating teams through the web app. Save new channels with Discord command /register channel."
      >
        <div className="overflow-x-auto">
          <div className="py-2 align-middle inline-block min-w-full">
            <div className="overflow-hidden sm:rounded-lg">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className={`rounded-left px-6 py-3 pr1 text-left text-xs font-medium tracking-wider`}
                      style={{ width: "30%" }}
                    >
                      Discord Server
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-medium tracking-wider`}
                      style={{ width: "30%" }}
                    >
                      Discord Channel
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-medium tracking-wider`}
                      style={{ width: "10%" }}
                    >
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg3" : ""}>
                      <td
                        className="rounded-left px-6 whitespace-nowrap text-sm font-medium"
                        style={{ height: 56 }}
                      >
                        <Tag text={channel.guildName} color="secondary" />
                      </td>
                      <td className="px-6 whitespace-nowrap text-sm weak-text">
                        <Tag text={`#${channel.channelName}`} color="secondary" />
                      </td>
                      <td className="rounded-right px-6 whitespace-nowrap text-sm weak-text">
                        <Button
                          text="Delete"
                          click={() => {
                            const newArr = copy(channels)
                            newArr.splice(i, 1)

                            toast.promise(
                              userAPI.updatePath(auth.userData.userID, "discordChannels", newArr),
                              {
                                loading: "Deleting channel",
                                success: "Channel deleted!",
                                error: (err) => `Error: ${err}`
                              }
                            )
                          }}
                          color="tertiary"
                          variant="soft"
                          size="small"
                          addClass="align-self-center"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </SettingSection>
    )
  }

  const TimeZoneSection = () => {
    return (
      <SettingSection
        title="Time Zone"
        description="Prepi will use this time zone when creating new signups with Discord bot."
      >
        <Popout
          noOptionsFoundText="No servers found."
          options={ianaTimeZones.map((o) => ({ label: o, value: o }))}
          defaultOption={
            auth?.userData?.settings?.timeZone
              ? {
                  value: auth.userData?.settings?.timeZone,
                  label: auth.userData?.settings?.timeZone
                }
              : {
                  label: "Choose time zone",
                  value: "n/a"
                }
          }
          minWidth={230}
          autoFocus
          isMulti={false}
          onChange={(option) => {
            toast.promise(
              userAPI.updatePath(auth.userData.userID, "settings.timeZone", option.value),
              {
                loading: "Saving time zone",
                success: "Time zone saved",
                error: (err) => `Couldn't save time zone. ${getErrorMessage(err)}`
              }
            )
          }}
        />
      </SettingSection>
    )
  }

  const ProfileNameSection = () => {
    const profileName = auth?.userData?.displayName
    const profileNameRef = useRef<string>(profileName || "")
    return (
      <SettingSection title="Profile" description="Will be used when shown as signup admin.">
        <div className="flex-col gap1 align-end">
          <span className="weak-text align-self-start">Profile name</span>
          <TextInput
            onChange={(event) => {
              profileNameRef.current = event.target.value
            }}
            placeholder="Profile name"
            defaultValue={profileNameRef.current}
          />
          <Button
            text="Save"
            click={() => {
              toast.promise(
                userAPI.updatePath(auth.userData.userID, "displayName", profileNameRef.current),
                {
                  loading: "Saving profile name",
                  success: "Profile name saved!",
                  error: (err) => `Error: ${err}`
                }
              )
            }}
          />
        </div>
      </SettingSection>
    )
  }

  const DeleteProfileBtn = () => {
    return (
      <Button
        text="Delete profile"
        click={() => {
          // if(raids?.some(raid => raid.owner.userID === auth?.userData?.userID)){
          //   toast.error("You're still owner on some raids. To delete your account you first have to promote someone else or delete your raids.")
          //   return
          // }
          // todo: check if premium user
          
          const deleteUser = async () => {
            // TODO: make work with production
            const res = await axios.delete(`/customer`)

            await auth.deleteCurrentUser()
          }

          const modal = confirmDangerModal("Delete profile", () => {
            toast.promise(
              deleteUser(),
              {
                loading: "Deleting user",
                success: "User deleted",
                error: (err) => `Couldn't delete user. ${getErrorMessage(err)}`
              }
            )
          })

          modal.middle =
            "This action is irreversable. Make sure to transfer ownership of any teams or signups first."
          modal.bottom = <ConfirmDangerModalBottom confirmBtnText="Delete profile" />
          triggerModal(modal)
        }}
        color="tertiary"
        variant="outline"
        size="small"
        addClass="align-self-start"
      />
    )
  }

  return (
    <>
      <ProfileNameSection />
      <TimeZoneSection />
      <DiscordChannels />
      <DeleteProfileBtn />
    </>
  )
}

export default GeneralSettings;